// components/use-case-details/use-case-workflow/workflow-renderer.tsx
import React from 'react';

import { AgentType } from './agent-type-config';
import { getWorkflowComponentByType } from './workflow-component-registry';

interface ComponentDetail {
  title?: string;
  description?: string;
  agent_type?: AgentType;
  bullet_points?: string[];
  model?: string;
  temperature?: number | string;
  trigger?: string;
  output?: string;
  store_type?: string;
  database_name?: string;
  embedding_model?: string;
  dimensions?: number;
  metric_type?: string;
  instructions?: string[];
  required_skills?: string[];
  approval_type?: string;
  tool_type?: string;
  tool_name?: string;
  website_url?: string;
  parameters?: Record<string, any>;
  input_data_format?: string;
  output_data_format?: string;
  [key: string]: any;
}

interface Component {
  component_id: string;
  component_type: string;
  [key: string]: any;
  // Specific details based on component type
  agent_details?: ComponentDetail;
  data_store_details?: ComponentDetail;
  data_source_details?: ComponentDetail;
  tool_details?: ComponentDetail;
  human_task_details?: ComponentDetail;
  data_transformation_details?: ComponentDetail;
}

interface Step {
  step_id: string;
  description: string;
  actor: string;
  hasDecision?: boolean;
  inputs?: any[];
  outputs?: any[];
  components?: Component[];
}

interface ProcessOverview {
  title: string;
  steps: Step[];
}

interface Example {
  title: string;
  description: string;
  user_query: string;
  ai_thinking_text: string;
  ai_response_intro: string;
  results: any[];
  ai_response_outro: string;
}

interface BenefitsAndInsights {
  title: string;
  description: string;
  benefits: any[];
  insights: string[];
}

interface Workflow {
  workflow_id?: string;
  id?: string;
  title: string;
  description: string;
  process_overview: ProcessOverview;
  overall_components?: Component[];
  examples?: Example[];
  benefits_and_insights?: BenefitsAndInsights;
}

interface WorkflowRendererProps {
  workflow: Workflow;
}

export const WorkflowRenderer: React.FC<WorkflowRendererProps> = ({ workflow }) => {
  // Safety check for empty workflow
  if (!workflow) {
    return (
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <h3 className="text-xl text-red-400">No workflow data available</h3>
        <p className="text-gray-400 mt-2">The workflow data could not be loaded or is empty.</p>
      </div>
    );
  }
  
  // Normalize workflow ID (handle both id and workflow_id properties)
  const workflowId = workflow.id || workflow.workflow_id;
  
  // Function to determine icon based on actor
  const getActorIcon = (actor: string): 'ai' | 'human' | 'system' => {
    if (!actor) return 'system';
    
    switch (actor.toLowerCase()) {
      case 'ai':
        return 'ai';
      case 'human':
        return 'human';
      case 'system':
        return 'system';
      default:
        return 'system';
    }
  };

  // Function to get color based on actor
  const getActorColor = (actor: string): string => {
    if (!actor) return 'bg-gray-600';
    
    switch (actor.toLowerCase()) {
      case 'ai':
        return 'bg-green-600';
      case 'human':
        return 'bg-blue-600';
      case 'system':
        return 'bg-gray-600';
      default:
        return 'bg-gray-600';
    }
  };

  // Function to render component based on component type
  const renderComponent = (component: Component) => {
    if (!component) return null;
    
    // Extract the component type and appropriate details object
    const componentType = component.component_type;
    if (!componentType) {
      return (
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg mb-4">
          <h3 className="text-lg font-bold mb-2">Unknown Component</h3>
          <p className="text-gray-400">Component has no type</p>
        </div>
      );
    }
    
    let details: ComponentDetail | null = null;
    let mappedType = componentType;
    
    // Map component type to registry key and get the details object
    if (componentType === 'agent' && component.agent_details) {
      details = component.agent_details;
      mappedType = 'agentCard';
    } else if (componentType === 'data_source' && component.data_source_details) {
      details = component.data_source_details;
      mappedType = 'dataSourceCard';
    } else if (componentType === 'data_store' && component.data_store_details) {
      details = component.data_store_details;
      if (details.store_type === 'vector_db') {
        mappedType = 'vectorDBComponent';
      } else {
        mappedType = 'dataStoreCard'; // Default for non-vector data stores
      }
    } else if (componentType === 'tool' && component.tool_details) {
      details = component.tool_details;
      mappedType = 'toolCard';
    } else if (componentType === 'human_task' && component.human_task_details) {
      details = component.human_task_details;
      mappedType = 'hitlComponent';
    } else if (componentType === 'data_transformation' && component.data_transformation_details) {
      details = component.data_transformation_details;
      mappedType = 'toolCard'; // Map transformation to tool card
    }
    
    // If details are found in frontend_component_type, use that instead
    if (component.frontend_component_type && typeof component.frontend_component_type === 'string') {
      mappedType = component.frontend_component_type;
    }
    
    // If no details found, try to extract from component directly (flat structure)
    if (!details) {
      details = component as unknown as ComponentDetail;
    }

    // Get component from registry
    const Component = getWorkflowComponentByType(mappedType);
    
    // Prepare props based on component type
    switch (mappedType) {
      case 'agentCard':
        return (
          <Component
            title={details.title || 'Agent'}
            agentType={details.agent_type as AgentType || 'analysis'}
            description={details.description || ''}
            bulletPoints={details.bullet_points || []}
            model={details.model || 'Unknown Model'}
            temperature={details.temperature?.toString() || '0.7'}
            trigger={details.trigger || 'Manual'}
            output={details.output || 'Analysis Results'}
            borderColor="bg-purple-600"
          />
        );
      
      case 'vectorDBComponent':
        return (
          <Component 
            title={details.title || 'Vector Database'}
            description={details.description || ''}
            databaseType={details.database_name || details.databaseType || 'Vector DB'}
            embeddingModel={details.embedding_model || details.embeddingModel || 'Unknown Embedding'}
            dimensions={details.dimensions || 1536}
            metricType={details.metric_type || details.metricType || 'cosine'}
            exampleQuery={details.exampleQuery || "Sample query"}
            exampleResult={details.exampleResult || "Sample result"}
            industryContext={details.industryContext || "General"}
            borderColor="bg-blue-600"
            implementationSteps={details.implementationSteps || []} 
            implementationTips={details.implementationTips || []}
          />
        );
      
      case 'hitlComponent':
        return (
          <Component
            title={details.title || 'Human Task'}
            description={details.description || ''}
            approvalProcess={details.approvalProcess || [
              {
                stage: "Review",
                description: details.description || "Human expert reviews the output",
                actor: "human",
                decisionType: "binary"
              }
            ]}
            borderColor="bg-blue-600"
          />
        );

      case 'dataSourceCard':
        return (
          <Component
            title={details.title || 'Data Source'}
            description={details.description || ''}
            source_type={details.source_type}
            source_name={details.source_name}
            data_format={details.data_format}
            connection_details={details.connection_details}
            borderColor="bg-blue-600"
          />
        );
        
      case 'toolCard':
        return (
          <Component
            title={details.title || 'Tool'}
            description={details.description || ''}
            tool_type={details.tool_type}
            tool_name={details.tool_name}
            website_url={details.website_url}
            input_data_format={details.input_data_format}
            output_data_format={details.output_data_format}
            parameters={details.parameters}
            borderColor="bg-indigo-600"
          />
        );

      default:
        // Fallback for unrecognized component types
        return (
          <div className="p-4 border rounded-lg bg-gray-800 mb-4">
            <h3 className="text-xl font-bold">{details.title || 'Component'}</h3>
            <p className="text-gray-400">{details.description || ''}</p>
            <div className="mt-2 text-xs bg-gray-700 p-2 rounded">
              <pre>{JSON.stringify(details, null, 2)}</pre>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="workflow-renderer">
      {/* Process Overview Section */}
      {workflow.process_overview && (
        <div className="workflow-process-section mb-8">
          <h3 className="text-xl text-amber-500 font-bold mb-4">
            {workflow.process_overview.title || 'Process Overview'}
          </h3>
          
          {/* Process Steps Section with connecting lines */}
          <div className="workflow-steps-section relative">
            {/* Vertical connecting line that runs down the center of the step numbers */}
            <div className="absolute left-6 top-12 bottom-8 w-0.5 bg-gray-700" style={{ height: 'calc(100% - 4rem)' }} />
            
            {Array.isArray(workflow.process_overview.steps) && workflow.process_overview.steps.map((step, stepIndex) => {
              if (!step) return null;
              
              const actorColor = getActorColor(step.actor);
              const isLastStep = stepIndex === workflow.process_overview.steps.length - 1;
              
              // Find any overall components that belong to this step
              const stepOverallComponents = workflow.overall_components?.filter(comp => {
                return step.components?.some(stepComp => 
                  stepComp.component_id === comp.component_id
                );
              }) || [];
              
              return (
                <div key={`step-${step.step_id || stepIndex}`} className="workflow-step relative pb-12">
                  {/* Step Header */}
                  <div className="flex items-start mb-6">
                    <div className={`step-number w-12 h-12 rounded-full flex items-center justify-center mr-4 ${actorColor} z-10 border-2 border-gray-800`}>
                      <span className="text-white font-bold">{stepIndex + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{step.description || `Step ${stepIndex + 1}`}</h3>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-400 capitalize mr-2">
                          {step.actor || 'System'} Step
                        </span>
                        {step.hasDecision && (
                          <span className="text-xs bg-yellow-800 text-yellow-300 px-2 py-0.5 rounded">
                            Requires Decision
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Show components - fix duplicate rendering issue */}
                  <div className="ml-16 space-y-4 mb-6">
                    {/* Only render step.components - don't also render overall_components */}
                    {step.components && Array.isArray(step.components) && step.components.length > 0 && 
                      step.components.map((component, index) => {
                        if (!component) return null;
                        
                        // Find the full component details from overall_components if available
                        const fullComponent = workflow.overall_components?.find(
                          comp => comp && comp.component_id === component.component_id
                        ) || component;
                        
                        return (
                          <div key={`component-${component.component_id || index}`}>
                            {renderComponent(fullComponent)}
                          </div>
                        );
                      })
                    }
                  </div>

                  {/* Input/Output Section */}
                  {((step.inputs && step.inputs.length > 0) || (step.outputs && step.outputs.length > 0)) && (
                    <div className="ml-16 grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Inputs */}
                      {step.inputs && Array.isArray(step.inputs) && step.inputs.length > 0 && (
                        <div className="bg-gray-800/50 p-4 rounded-lg">
                          <h4 className="text-sm font-semibold mb-2">Inputs</h4>
                          <ul className="space-y-1">
                            {step.inputs.map((input, inputIndex) => (
                              <li key={`input-${inputIndex}`} className="text-sm text-gray-400">
                                {input?.description || `Input ${inputIndex + 1}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Outputs */}
                      {step.outputs && Array.isArray(step.outputs) && step.outputs.length > 0 && (
                        <div className="bg-gray-800/50 p-4 rounded-lg">
                          <h4 className="text-sm font-semibold mb-2">Outputs</h4>
                          <ul className="space-y-1">
                            {step.outputs.map((output, outputIndex) => (
                              <li key={`output-${outputIndex}`} className="text-sm text-gray-400">
                                {output?.description || `Output ${outputIndex + 1}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Benefits Section */}
      {workflow.benefits_and_insights && (
        <div className="benefits-insights-section mb-8 mt-6">
          <h3 className="text-xl text-amber-500 font-bold mb-4">
            {workflow.benefits_and_insights.title || "Benefits & Insights"}
          </h3>
          <p className="text-gray-400 mb-6">{workflow.benefits_and_insights.description || ""}</p>
          
          {/* Benefits */}
          {workflow.benefits_and_insights.benefits && 
           Array.isArray(workflow.benefits_and_insights.benefits) && 
           workflow.benefits_and_insights.benefits.length > 0 && (
            <div className="mb-6">
              {(() => {
                const KeyBenefitsComponent = getWorkflowComponentByType('keyBenefitsCard');
                return (
                  <KeyBenefitsComponent
                    title="Key Benefits"
                    benefits={workflow.benefits_and_insights.benefits.map(benefit => ({
                      icon: benefit.icon || "CheckCircle",
                      title: benefit.title || "Benefit",
                      description: benefit.description || ""
                    }))}
                    iconColor="green"
                  />
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 